import React, { useCallback, useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import {
  MEDIA_WIDGET_CAPTION_MAX_LENGTH,
  MEDIA_WIDGET_LINK_MAX_LENGTH,
  MediaWidgetFileKind,
  MediaWidgetFit,
  MediaWidgetOptions,
  MediaWidgetShape,
} from 'fym-shared/src/smartlinks/mediaWidget';
import { TypedMemo } from '../../../../../common/types';
import { Segmented, SegmentedOption } from '../../../../../music24/components/primitives/Segmented';
import { mediaBlockMessages } from '../mediaBlockMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media block editor; each part uses its own slice of it */
import styles from './MediaWidgetEditionView.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MediaLookOptionsProps {
  kind: MediaWidgetFileKind | null;
  options: MediaWidgetOptions;
  setOption: <K extends keyof MediaWidgetOptions>(key: K, value: MediaWidgetOptions[K]) => void;
}

/** Look and text (design a2, a4): the shape, the fit of a video, the caption and "Tap opens". */
const MediaLookOptionsComponent: React.FC<MediaLookOptionsProps> = ({ kind, options, setOption }) => {
  const intl = useIntl();
  const id = useId();

  const shapes = useMemo<ReadonlyArray<SegmentedOption<MediaWidgetShape>>>(
    () => [
      { value: 'original', label: intl.formatMessage(mediaBlockMessages.shapeOriginal) },
      { value: 'square', label: '1:1' },
      { value: 'portrait', label: '4:5' },
      { value: 'wide', label: '16:9' },
    ],
    [intl]
  );
  const fits = useMemo<ReadonlyArray<SegmentedOption<MediaWidgetFit>>>(
    () => [
      { value: 'cover', label: intl.formatMessage(mediaBlockMessages.fitCover) },
      { value: 'contain', label: intl.formatMessage(mediaBlockMessages.fitContain) },
    ],
    [intl]
  );

  const setShape = useCallback((value: MediaWidgetShape) => setOption('shape', value), [setOption]);
  const setFit = useCallback((value: MediaWidgetFit) => setOption('fit', value), [setOption]);
  const setCaption = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setOption('caption', event.target.value === '' ? null : event.target.value),
    [setOption]
  );
  const setLink = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setOption('linkUrl', event.target.value === '' ? null : event.target.value),
    [setOption]
  );

  return (
    <section className={styles.section} data-testid="media-look">
      <h6 className={styles.sectionTitle}>{intl.formatMessage(mediaBlockMessages.look)}</h6>
      <span className={styles.fieldName}>{intl.formatMessage(mediaBlockMessages.shape)}</span>
      <Segmented
        options={shapes}
        value={options.shape}
        onChange={setShape}
        ariaLabel={intl.formatMessage(mediaBlockMessages.shape)}
      />
      {/* An image always fills its shape (design a4). */}
      {kind === 'video' && (
        <>
          <span className={styles.fieldName}>{intl.formatMessage(mediaBlockMessages.fit)}</span>
          <Segmented
            options={fits}
            value={options.fit}
            onChange={setFit}
            ariaLabel={intl.formatMessage(mediaBlockMessages.fit)}
          />
        </>
      )}
      <label className={styles.fieldName} htmlFor={`${id}-caption`}>
        {intl.formatMessage(mediaBlockMessages.caption)}
      </label>
      <Input
        id={`${id}-caption`}
        value={options.caption ?? ''}
        maxLength={MEDIA_WIDGET_CAPTION_MAX_LENGTH}
        placeholder={intl.formatMessage(mediaBlockMessages.captionPlaceholder)}
        onChange={setCaption}
        data-testid="media-caption"
      />
      <label className={styles.fieldName} htmlFor={`${id}-link`}>
        {intl.formatMessage(mediaBlockMessages.link)}
      </label>
      {/* A text field, not `type="url"`: the browser check of a url field stops the submit with no
          line of ours. The block rule (http or https only) shows its own line under the form. */}
      <Input
        id={`${id}-link`}
        type="text"
        inputMode="url"
        autoComplete="url"
        value={options.linkUrl ?? ''}
        maxLength={MEDIA_WIDGET_LINK_MAX_LENGTH}
        placeholder="https://"
        onChange={setLink}
        data-testid="media-link"
      />
      <span className={styles.note}>{intl.formatMessage(mediaBlockMessages.linkNote)}</span>
    </section>
  );
};

export const MediaLookOptions = TypedMemo(MediaLookOptionsComponent);
